import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { useTenantConfig } from '@webmono/storefront-ui/providers'
import type { MenuItem, MenuCategory, DietaryTag } from '@webmono/storefront-ui/types'
import { formatCents, cn } from '@webmono/storefront-ui/utils'
import { DIETARY_LABELS } from '@webmono/storefront-ui/types'
import { useMenu } from '@/lib/hooks'
import { EmptyState, EmptySearchIcon } from '@/components/EmptyState'

const DIETARY_FILTERS: DietaryTag[] = ['gf', 'v', 'vg', 'df']

function MenuItemCard({ item, index }: { item: MenuItem; index: number }) {
  const tenant = useTenantConfig()

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, delay: index * 0.05 }}
    >
      <Link
        to={`/menu/${item.id}`}
        className={cn(
          'flex gap-3 p-3 rounded-lg transition-all',
          item.available
            ? 'bg-card hover:bg-muted/50 active:scale-[0.98]'
            : 'bg-muted/30 opacity-60'
        )}
      >
      {item.image && (
        <img
          src={item.image}
          alt={item.name}
          className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
        />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-medium text-foreground leading-tight">{item.name}</h3>
          <span className="font-mono text-sm font-medium text-foreground flex-shrink-0">
            {formatCents(item.priceCents, tenant.operations.currency)}
          </span>
        </div>
        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{item.description}</p>
        <div className="flex items-center gap-2 mt-2">
          {item.dietaryTags.map((tag) => (
            <span
              key={tag}
              className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground"
              title={DIETARY_LABELS[tag]?.description}
            >
              {DIETARY_LABELS[tag]?.label}
            </span>
          ))}
          {!item.available && (
            <span className="text-xs px-2 py-0.5 rounded bg-destructive/10 text-destructive font-medium">
              Sold Out
            </span>
          )}
          {item.popular && item.available && (
            <span className="text-xs px-2 py-0.5 rounded bg-accent/10 text-accent font-medium">
              Popular
            </span>
          )}
        </div>
      </div>
    </Link>
    </motion.div>
  )
}

function CategorySection({ category }: { category: MenuCategory }) {
  const visibleItems = category.items.filter((item) => !item.hidden)

  if (visibleItems.length === 0) return null

  return (
    <section id={`category-${category.id}`} className="scroll-mt-32">
      <div className="sticky top-[60px] bg-background/95 backdrop-blur-sm z-10 py-3 -mx-4 px-4">
        <h2 className="font-heading font-semibold text-lg text-foreground">{category.name}</h2>
        {category.description && (
          <p className="text-sm text-muted-foreground mt-0.5">{category.description}</p>
        )}
      </div>
      <div className="space-y-2 mt-2">
        {visibleItems.map((item, index) => (
          <MenuItemCard key={item.id} item={item} index={index} />
        ))}
      </div>
    </section>
  )
}

export function MenuPage() {
  const tenant = useTenantConfig()
  const { data: menu, isLoading } = useMenu()
  const [search, setSearch] = useState('')
  const [activeDietaryFilters, setActiveDietaryFilters] = useState<DietaryTag[]>([])

  const filteredCategories = useMemo(() => {
    if (!menu) return []

    return menu.categories
      .map((category) => ({
        ...category,
        items: category.items.filter((item) => {
          const matchesSearch =
            search === '' ||
            item.name.toLowerCase().includes(search.toLowerCase()) ||
            item.description.toLowerCase().includes(search.toLowerCase())

          const matchesDietary =
            activeDietaryFilters.length === 0 ||
            activeDietaryFilters.every((filter) => item.dietaryTags.includes(filter))

          return matchesSearch && matchesDietary
        }),
      }))
      .filter((category) => category.items.length > 0)
  }, [menu, search, activeDietaryFilters])

  const toggleDietaryFilter = (tag: DietaryTag) => {
    setActiveDietaryFilters((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    )
  }

  if (isLoading) {
    return (
      <div className="max-w-lg mx-auto px-4 py-6">
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="h-6 bg-muted rounded w-1/3 mb-3" />
              <div className="space-y-2">
                {[1, 2].map((j) => (
                  <div key={j} className="flex gap-3 p-3">
                    <div className="w-20 h-20 bg-muted rounded-lg" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-muted rounded w-2/3" />
                      <div className="h-3 bg-muted rounded w-full" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-lg mx-auto px-4 py-4">
      <div className="relative mb-4">
        <input
          type="search"
          placeholder="Search menu..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />
        <svg
          className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </div>

      {tenant.features.dietaryFilters && (
        <div className="flex gap-2 mb-4 overflow-x-auto pb-2 scrollbar-hide -mx-4 px-4">
          {DIETARY_FILTERS.map((tag) => (
            <button
              key={tag}
              onClick={() => toggleDietaryFilter(tag)}
              className={cn(
                'flex-shrink-0 px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
                activeDietaryFilters.includes(tag)
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              )}
            >
              {DIETARY_LABELS[tag]?.label}
            </button>
          ))}
        </div>
      )}

      {menu && menu.categories.length > 1 && (
        <div className="flex gap-2 mb-4 overflow-x-auto pb-2 scrollbar-hide -mx-4 px-4">
          {menu.categories.map((category) => (
            <a
              key={category.id}
              href={`#category-${category.id}`}
              className="flex-shrink-0 px-3 py-1.5 rounded-full text-sm font-medium bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-colors"
            >
              {category.name}
            </a>
          ))}
        </div>
      )}

      <div className="space-y-6">
        {filteredCategories.map((category) => (
          <CategorySection key={category.id} category={category} />
        ))}
      </div>

      {filteredCategories.length === 0 && (
        <EmptyState
          icon={<EmptySearchIcon />}
          title="No items found"
          description={search ? `No results for "${search}"` : 'No items match your filters'}
          action={{
            label: 'Clear filters',
            onClick: () => {
              setSearch('')
              setActiveDietaryFilters([])
            },
          }}
        />
      )}
    </div>
  )
}
